import { Link, useParams } from "@tanstack/react-router";
import type { RiskModelType } from "@unbox/db";
import { AlertCircle, ArrowDown, ChevronDown, InfoCircle } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { settingsMerchantDetailsRoute } from "@/router";
import { cx } from "@/utils/cx";
import {
  humanizeKey,
  KEYWORD_FIELD_LABELS,
  keywordFieldsFor,
  MERCHANT_MANAGED_PLACEHOLDERS,
  OWN_BRANDS_LABEL_SUFFIX,
  orderKeywordKeys,
  parseKeywordList,
} from "./keyword-config";
import type { Severity } from "./risk-groups";
import {
  CORE_SAFETY_LOCKED_TOOLTIP,
  CREATOR_RISK_EXPLANATION_TOOLTIP,
  canEditCreatorRiskExplanation,
  RISK_EXPLANATION_DEFAULT_PLACEHOLDERS,
  SEVERITY_META,
  SEVERITY_ORDER,
} from "./risk-groups";

const GENERIC_CREATOR_RISK_EXPLANATION_PLACEHOLDER =
  "Explain what's not allowed for this risk. This becomes the {creator_risk_explanation} variable you can insert into creator messages.";

export type ModalRiskRowItem = {
  key: string;
  name: string;
  desc: string;
  // DB risk category — see canEditCreatorRiskExplanation for which categories/risks can
  // set customCreatorRiskExplanation.
  category: string;
  enabled: boolean;
  // Whether the risk is currently enabled in the saved config (independent of
  // the in-session toggle). Used to warn only when turning off a live risk.
  wasEnabled: boolean;
  autoConfirm: boolean;
  severity: Severity;
  // The safety policy this risk is mapped to, or null when unassigned.
  safetyPolicyId: string | null;
  modelType: RiskModelType;
  // Current values of the model type's keyword (stringArray) config fields.
  config: Record<string, string[]>;
  // Domain-knowledge keys referenced as `{placeholder}` in this risk's config.
  placeholderKeys: string[];
  // Merchant-authored override for the {creator_risk_explanation} message
  // variable, or null when not set.
  customCreatorRiskExplanation: string | null;
};

export type SafetyPolicyOption = { id: string; label: string; type: string };

const NO_POLICY_KEY = "__none__";

const SEVERITY_OPTIONS = SEVERITY_ORDER.map((value) => ({ value, label: SEVERITY_META[value].label }));

// A tag-style editor for a list of keyword strings.
const KeywordInput = ({
  label,
  values,
  onChange,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
}) => {
  const [draft, setDraft] = useState("");

  const add = (val: string) => {
    const trimmed = val.trim();
    if (trimmed && !values.includes(trimmed)) onChange([...values, trimmed]);
    setDraft("");
  };

  return (
    <div>
      <p className="text-sm font-medium text-secondary mb-2">
        {label} <span className="text-error-500">*</span>
      </p>
      <div className="flex flex-wrap gap-1.5 p-2.5 min-h-[44px] border border-border-primary rounded-md bg-white">
        {values.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border-primary bg-white text-sm text-secondary font-medium"
          >
            {tag}
            <button
              type="button"
              className="text-quaternary hover:text-primary transition-colors"
              onClick={() => onChange(values.filter((t) => t !== tag))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          placeholder="Add keyword, press enter to submit..."
          className="flex-1 min-w-[80px] text-sm outline-none bg-transparent text-primary placeholder:text-quaternary"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            }
          }}
          onBlur={() => add(draft)}
        />
      </div>
    </div>
  );
};

// Read-only view of a keyword list owned by the Merchant Details page (e.g.
// `own_brands`). Values can't be edited here — the note links back to where they
// are managed, since the same list is shared across the app.
const ManagedBrandList = ({ label, values, merchantId }: { label: string; values: string[]; merchantId: string }) => (
  <div>
    <p className="text-sm font-medium text-secondary mb-2">{label}</p>
    <div className="flex flex-wrap gap-1.5 p-2.5 min-h-[44px] border border-border-primary rounded-md bg-disabled_subtle">
      {values.length > 0 ? (
        values.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center px-2 py-0.5 rounded border border-border-primary bg-white text-sm text-secondary font-medium"
          >
            {tag}
          </span>
        ))
      ) : (
        <span className="text-sm text-tertiary self-center">No associated brands added yet.</span>
      )}
    </div>
    <p className="mt-2 rounded-lg bg-unbox-cream-50 p-4 text-xs text-tertiary leading-relaxed">
      {"This list is shared across the app, not just this risk model. To add or edit brands, "}
      <Link
        to={settingsMerchantDetailsRoute.to}
        params={{ merchantId }}
        className="font-normal text-secondary underline decoration-dotted underline-offset-2"
      >
        go to Merchant details
      </Link>
      {' and edit the "Associated brands".'}
    </p>
  </div>
);

type ModalRiskRowProps = {
  item: ModalRiskRowItem;
  policies: SafetyPolicyOption[];
  domainKnowledge: Record<string, string>;
  onChange: (
    patch: Partial<
      Pick<
        ModalRiskRowItem,
        "enabled" | "autoConfirm" | "severity" | "safetyPolicyId" | "config" | "customCreatorRiskExplanation"
      >
    >,
  ) => void;
  onDomainKnowledgeChange: (key: string, values: string[]) => void;
  /** Merchant Details owns `own_brands` and shows it read-only here. Nothing owns
   *  it above a merchant, so a workspace template edits it inline. */
  managedPlaceholdersEditable?: boolean;
  /** Set when the modal was opened to switch this risk on or off: the row is scrolled to and highlighted. */
  focusAction?: "on" | "off";
};

export const ModalRiskRow = ({
  item,
  policies,
  domainKnowledge,
  onChange,
  onDomainKnowledgeChange,
  managedPlaceholdersEditable = false,
  focusAction,
}: ModalRiskRowProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusAction) rowRef.current?.scrollIntoView({ block: "center" });
  }, [focusAction]);
  const { merchantId } = useParams({ strict: false }) as { merchantId?: string };
  const keywordFields = keywordFieldsFor(item.modelType);
  // `own_brands` and friends are shown read-only (managed on Merchant Details);
  // the rest are inline-editable keyword lists. Ordered to match Figma.
  const orderedPlaceholders = orderKeywordKeys(item.placeholderKeys);
  const isManaged = (key: string) => !managedPlaceholdersEditable && MERCHANT_MANAGED_PLACEHOLDERS.has(key);
  const managedPlaceholders = orderedPlaceholders.filter(isManaged);
  const editablePlaceholders = orderedPlaceholders.filter((k) => !isManaged(k));
  const hasInputs = item.enabled && (keywordFields.length > 0 || item.placeholderKeys.length > 0);
  const isLocked = item.category === "baseline";

  return (
    <div
      ref={rowRef}
      className={cx("border-b border-border-secondary last:border-b-0", focusAction ? "bg-brand-50 px-6" : "mx-6")}
    >
      {focusAction && (
        <p className="flex items-center gap-1 pt-3 text-sm font-medium text-brand-secondary">
          <ArrowDown aria-hidden="true" className="size-4" />
          Switch this risk {focusAction}, then save
        </p>
      )}
      <div className="flex items-start gap-4 py-4">
        {/* Toggle */}
        <div className="shrink-0">
          {isLocked ? (
            <Tooltip title={CORE_SAFETY_LOCKED_TOOLTIP}>
              <Toggle isSelected={item.enabled} isDisabled size="sm" color="success" />
            </Tooltip>
          ) : (
            <Toggle
              isSelected={item.enabled}
              onChange={(val) => onChange({ enabled: val })}
              size="sm"
              color="success"
            />
          )}
        </div>

        {/* Name + severity + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <p
              className={cx(
                "flex-1 min-w-0 truncate text-sm font-semibold",
                item.enabled ? "text-primary" : "text-tertiary",
              )}
            >
              {item.name}
            </p>

            {item.enabled && (
              <Dropdown.Root>
                <AriaButton className="flex items-center gap-2 px-2.5 py-1 rounded-md border border-border-primary bg-white text-xs font-medium outline-none cursor-pointer shrink-0">
                  <span className="whitespace-nowrap" style={{ color: SEVERITY_META[item.severity].color }}>
                    {SEVERITY_OPTIONS.find((o) => o.value === item.severity)?.label} Severity
                  </span>
                  <ChevronDown aria-hidden="true" className="w-3.5 h-3.5 text-quaternary shrink-0" />
                </AriaButton>
                <Dropdown.Popover>
                  <Dropdown.Menu
                    selectedKeys={new Set([item.severity])}
                    onSelectionChange={(keys) => {
                      const val = [...keys][0] as ModalRiskRowItem["severity"];
                      if (val) onChange({ severity: val });
                    }}
                  >
                    {SEVERITY_OPTIONS.map((opt) => (
                      <Dropdown.Item key={opt.value} id={opt.value} textValue={`${opt.label} Severity`}>
                        <span style={{ color: SEVERITY_META[opt.value].color }}>{opt.label} Severity</span>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.Root>
            )}
          </div>
          <p className="text-xs text-tertiary mt-1 leading-relaxed">{item.desc}</p>

          {item.enabled && canEditCreatorRiskExplanation(item) && (
            <div className="mt-3">
              <label
                htmlFor={`custom-creator-risk-explanation-${item.key}`}
                className="flex items-center gap-1 text-xs font-medium text-secondary mb-1.5"
              >
                Risk explanation for creators
                <Tooltip title={CREATOR_RISK_EXPLANATION_TOOLTIP}>
                  <TooltipTrigger className="cursor-pointer text-fg-disabled hover:text-fg-quaternary_hover">
                    <InfoCircle className="w-3.5 h-3.5" />
                  </TooltipTrigger>
                </Tooltip>
              </label>
              <textarea
                id={`custom-creator-risk-explanation-${item.key}`}
                className="w-[226%] min-h-[80px] px-3 py-2 rounded-md border border-border-primary text-sm text-primary placeholder:text-quaternary resize-y"
                placeholder={GENERIC_CREATOR_RISK_EXPLANATION_PLACEHOLDER}
                value={item.customCreatorRiskExplanation ?? RISK_EXPLANATION_DEFAULT_PLACEHOLDERS[item.key] ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const riskDefault = RISK_EXPLANATION_DEFAULT_PLACEHOLDERS[item.key];
                  const isRealOverride = value !== riskDefault && !(value === "" && riskDefault === undefined);
                  onChange({ customCreatorRiskExplanation: isRealOverride ? value : null });
                }}
              />
            </div>
          )}
        </div>

        {/* Right controls — only when enabled */}
        {item.enabled && (
          <div className="flex items-start gap-8 shrink-0">
            {/* When risk is detected */}
            <div className="flex flex-col gap-1.5">
              <span className="text-sm">When risk is detected</span>
              <div className="inline-flex items-center gap-1 rounded-full bg-tertiary p-1">
                {(
                  [
                    { value: true, label: "Auto-approve" },
                    { value: false, label: "Require review" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    className={cx(
                      "px-3 py-1 rounded-full text-xs font-medium transition-colors",
                      item.autoConfirm === opt.value
                        ? "bg-primary-solid text-primary_on-brand shadow-sm"
                        : "text-secondary",
                    )}
                    onClick={() => onChange({ autoConfirm: opt.value })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* If risk is confirmed */}
            <div className="flex flex-col gap-1.5">
              <span className="text-sm ">If risk is confirmed</span>
              <Dropdown.Root>
                <AriaButton className="flex items-center justify-between gap-2 w-[220px] px-3 py-1.5 rounded-md border border-border-primary bg-primary text-xs font-medium text-primary outline-none cursor-pointer">
                  <span className="truncate">
                    {policies.find((p) => p.id === item.safetyPolicyId)?.label ?? "Do nothing"}
                  </span>
                  <ChevronDown aria-hidden="true" className="w-3 h-3 text-quaternary shrink-0" />
                </AriaButton>
                <Dropdown.Popover>
                  <Dropdown.Menu
                    selectedKeys={new Set([item.safetyPolicyId ?? NO_POLICY_KEY])}
                    onSelectionChange={(keys) => {
                      const val = [...keys][0] as string;
                      onChange({ safetyPolicyId: val === NO_POLICY_KEY ? null : val });
                    }}
                  >
                    <Dropdown.Item key={NO_POLICY_KEY} id={NO_POLICY_KEY} label="Do nothing" />
                    {policies.map((p) => (
                      <Dropdown.Item key={p.id} id={p.id} label={p.label} />
                    ))}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.Root>
            </div>
          </div>
        )}
      </div>

      {focusAction === "on" && !item.wasEnabled && item.enabled && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-md bg-warning-50 text-warning-700 text-xs font-medium">
          <AlertCircle aria-hidden="true" className="w-3.5 h-3.5 shrink-0" />
          Before you save, check what happens when it's detected and which policy runs if it's confirmed.
        </div>
      )}

      {/* Warning only when turning off a currently-live risk (pending disable) */}
      {item.wasEnabled && !item.enabled && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-md bg-warning-50 text-warning-700 text-xs font-medium">
          <AlertCircle aria-hidden="true" className="w-3.5 h-3.5 shrink-0" />
          Turning this off means your content will no longer be monitored for this risk.
        </div>
      )}

      {/* Keyword + placeholder inputs — driven by the model type's config schema */}
      {hasInputs && (
        <div className="pb-4 ml-10 flex flex-col gap-4">
          {keywordFields.map((field) => (
            <KeywordInput
              key={field}
              label={KEYWORD_FIELD_LABELS[field] ?? humanizeKey(field)}
              values={item.config[field] ?? []}
              onChange={(next) => onChange({ config: { ...item.config, [field]: next } })}
            />
          ))}
          {managedPlaceholders.map((key) => {
            const base = KEYWORD_FIELD_LABELS[key] ?? humanizeKey(key);
            const suffix = key === "own_brands" ? OWN_BRANDS_LABEL_SUFFIX[item.key] : undefined;
            return (
              <ManagedBrandList
                key={key}
                label={suffix ? `${base}. ${suffix}` : base}
                values={parseKeywordList(domainKnowledge[key])}
                merchantId={merchantId ?? ""}
              />
            );
          })}
          {editablePlaceholders.map((key) => (
            <KeywordInput
              key={key}
              label={KEYWORD_FIELD_LABELS[key] ?? humanizeKey(key)}
              values={parseKeywordList(domainKnowledge[key])}
              onChange={(next) => onDomainKnowledgeChange(key, next)}
            />
          ))}

          {item.modelType === "keyword" && (
            <div className="flex gap-10 items-start p-4 rounded-xl bg-warning-primary">
              <div className="flex items-center gap-1.5 shrink-0">
                <InfoCircle aria-hidden="true" className="w-5 h-5 text-secondary" />
                <span className="text-sm font-medium text-secondary whitespace-nowrap">Note</span>
              </div>
              <p className="flex-1 text-sm text-secondary">
                Keywords added above will appear as options for all merchants.
                <br />
                Each brand sees them as suggestions when they configure this risk, they can adopt them, ignore them or
                add their own.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
